import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import type { QueryClient } from '@tanstack/react-query';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import stableStringify from '@cotera/client/app/etc/stringify';
import { DocumentModel } from './document.model';
import { ResourceType } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { toDocumentApiData } from './use-document-query';
import { isSaveArtifactsApplicable } from '@cotera/client/v0/chat/artifact-dirty-state';

function hasSameSerializedEditorState(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

type Params = {
  silent?: boolean;
  /** Parity with {@link SaveAgentAction}; document `update` has no folder param yet. */
  folderId?: string;
  /**
   * Resolve the model to save by id (silent autosave / multi-artifact save)
   * instead of the targeted one. Falls back to targeted when unset.
   */
  documentId?: string;
};

export class SaveDocumentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Document';
  description = 'Save document changes';
  inputSchema = z.object({
    silent: z.boolean().optional(),
    folderId: z.string().uuid().optional(),
    documentId: z.string().optional(),
  });
  shortcut = ['ctrl', 's'];
  group = 'Document';
  icon = 'save';

  private _lastSavedDocumentId: string | null = null;
  private _lastSaveWasNew: boolean = false;
  /** Autosaves / navigation saves pass silent:true; we don't track those. */
  private _lastSaveWasSilent: boolean = false;

  constructor(
    private readonly client: TenantedClient['resource']['document'],
    private readonly queryClient: QueryClient,
    private readonly navigate: (
      path: string,
      options?: { replace?: boolean }
    ) => void,
    private readonly orgSlug: string,
    private readonly defaultFolderId?: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    // Chat-level Save Artifacts owns Ctrl+S when any open artifact is dirty.
    if (isSaveArtifactsApplicable(context)) {
      return false;
    }
    return context.hasTargetedModelOfType(DocumentModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const doc =
      params.documentId !== undefined
        ? context.getInScopeModelOfType(DocumentModel, params.documentId)
        : context.targetedModelOfType(DocumentModel);
    if (doc === null) {
      return ok({});
    }
    if (doc.lockedWatchable.snapshot()) {
      return err([{ t: 'error', message: 'Document is currently locked' }]);
    }

    const isNew = doc.isNew;
    this._lastSaveWasNew = isNew;
    const silent = params.silent === true;
    this._lastSaveWasSilent = silent;
    const saveToast = silent
      ? null
      : toast.loading('Saving document...', { id: 'save-document' });

    try {
      const editorStateToSave = doc.getEditorStateForSave();
      const existingDocument = doc.getDocumentForSave();
      if (
        hasSameSerializedEditorState(
          existingDocument.editorState,
          editorStateToSave
        )
      ) {
        doc.markAsSaved(editorStateToSave);
        return ok({});
      }
      const knownUpdatedAt = existingDocument.updatedAt;
      const folderId = params.folderId ?? this.defaultFolderId;
      const result = isNew
        ? await this.client.create({
            id: doc.documentId,
            editorState: editorStateToSave,
            ...(folderId !== undefined && { folderId }),
          })
        : await this.client.update({
            id: doc.documentId,
            editorState: editorStateToSave,
            writtenAt: knownUpdatedAt,
          });

      const updated = Assert.assertOk(result);
      const saveApplied = hasSameSerializedEditorState(
        updated.editorState,
        editorStateToSave
      );
      if (!saveApplied) {
        const conflictResourceQueryKey = queryKeys.resource.get({
          orgId: updated.orgId,
          type: ResourceType.Document,
          id: updated.id,
        });
        await this.queryClient.invalidateQueries({
          queryKey: conflictResourceQueryKey,
        });
        throw new Error(
          'Newer document changes were detected. Please review and save again.'
        );
      }
      this._lastSavedDocumentId = updated.id;
      doc.markAsSaved(editorStateToSave);
      if (!silent && saveToast !== null) {
        saveToast.success(`Document ${updated.title ?? 'Untitled'} saved`);
      }
      const data = toDocumentApiData(updated);
      doc.applySavedDocument(data);
      if (isNew) {
        // The server row now exists; subsequent saves must use the update path
        // with optimistic concurrency rather than re-upserting via create.
        doc.markNoLongerNew();
      }
      const resourceQueryKey = queryKeys.resource.get({
        orgId: updated.orgId,
        type: ResourceType.Document,
        id: updated.id,
      });
      await this.queryClient.invalidateQueries({ queryKey: resourceQueryKey });

      return ok({});
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save document';
      if (!silent && saveToast !== null) {
        saveToast.error(message);
      }
      return err([{ t: 'error', message }]);
    }
  }

  track(): ActionTrackMetadata | null {
    // Skip autosaves / navigation saves — only track deliberate user saves.
    if (this._lastSavedDocumentId === null || this._lastSaveWasSilent) {
      return null;
    }
    return {
      event: CocoEvent.DocumentSaved,
      properties: {
        documentId: this._lastSavedDocumentId,
        isNew: this._lastSaveWasNew,
      },
    };
  }
}
